import { axiosConfigBuilder } from '..';

/**
 * Service class for interacting with SDMMM API endpoints.
 *
 * This service provides methods for requesting and updating
 * data related to the SDMMM module.
 */
export class SDMMMService {
  /**
   * Retrieves SDMMM data comp details.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} retrieveDataRequestParams - Parameters to specify the data comp retrieval.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the data comp.
   */
  async retrieveDataComp(token, retrieveDataRequestParams) {
    const dataCompResponse = await axiosConfigBuilder(token).post(
      '/retrieve_data_comp',
      retrieveDataRequestParams
    );
    return dataCompResponse.data;
  }

  /**
   * Retrieves media dropdown options based on the provided request parameters.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} mediaDropdownRequestParams - Parameters to specify which media options to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the media dropdown options.
   */
  async getMediaDropdown(token, mediaDropdownRequestParams) {
    const mediaDropdownDataResponse = await axiosConfigBuilder(token).post(
      '/get_media_dropdown',
      mediaDropdownRequestParams
    );
    return mediaDropdownDataResponse.data;
  }

  /**
   * Downloads client data sheets from company-controlled or Arima-controlled database.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} downloadClientDataSheetsRequestParams - Parameters specifying which client data sheets to download.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the downloaded client data sheets.
   */
  async downloadClientDataSheets(token, downloadClientDataSheetsRequestParams) {
    const downloadResponse = await axiosConfigBuilder(token).post(
      '/download_client_integration_data',
      downloadClientDataSheetsRequestParams
    );
    return downloadResponse.data;
  }

  /**
   * Builds a tab sheet using the specified request parameters.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} buildTabSheetRequestParams - Parameters required for building the tab sheet.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the built tab sheet.
   */
  async buildTabSheet(token, buildTabSheetRequestParams) {
    const buildTabSheetDataResponse = await axiosConfigBuilder(token).post(
      '/build_tab_sheet',
      buildTabSheetRequestParams
    );
    return buildTabSheetDataResponse.data;
  }

  /**
   * Pulls tab sheet data based on the provided request parameters.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} pullTabSheetRequestParams - Parameters required for pulling the tab sheet data.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the pulled tab sheet.
   */
  async pullTabSheet(token, pullTabSheetRequestParams) {
    const pullTabSheetDataResponse = await axiosConfigBuilder(token).post(
      '/pull_tab_sheet',
      pullTabSheetRequestParams
    );
    return pullTabSheetDataResponse.data;
  }

  /**
   * Generates a signed URL for file upload using the specified request parameters.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} urlRequestParams - Parameters required for generating the signed URL.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the signed URL.
   */
  async generateSignedUrlUpload(token, urlRequestParams) {
    const signedUrlResponse = await axiosConfigBuilder(token).post(
      '/generate_signed_url_upload',
      urlRequestParams
    );
    return signedUrlResponse.data;
  }

  /**
   * Updates the SDMMM data comp using the provided request parameters.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} updateDataRequestParams - Parameters required for updating the data comp.
   * @returns {Promise<Object>} A promise that resolves to the response data confirming the update.
   */
  async updateDataComp(token, updateDataRequestParams) {
    const updateDataResponse = await axiosConfigBuilder(token).post(
      '/update_data_comp',
      updateDataRequestParams
    );
    return updateDataResponse.data;
  }

  /**
   * Builds a model using the specified request parameters.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} buildRequestParams - Parameters required for building the model.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the built model.
   */
  async buildModel(token, buildRequestParams) {
    const buildModelResponse = await axiosConfigBuilder(token).post(
      '/build_mmm_model',
      buildRequestParams
    );
    return buildModelResponse.data;
  }
}
